//================================================================
//  Component: Admin User Groups Pane ~ Child component of 'AdminUserTable.js'
//================================================================

//  Purpose: Generic Pane that fakes the list of users of groups. Allow's users to search the list.

//  Properties:
//    - title = {String, group name}
//    - defaultUsersQuery = {Array, query of the default users. All query's are limited to 100 results. Index WILL be required due to sorting.}
//    - paneOpen = {useState, used to determine if the modal is open/closed}
//    - setPaneOpen = {useState, used to determine if the modal is open/closed}


//  Example:
//    <AdminUserGroupsPane
//      title={'Lendlease Users'}
//      defaultUsersQuery={['usertype', '==', 'lendleaseuser']}
//      paneOpen = {paneOpen}
//      setPaneOpen = {setPaneOpen}
//    ></AdminUserGroupsPane>    

//================================================================


//Libraries
import React, { useState, useEffect, useReducer } from 'react';

//Functions
import QueryDocument from '../../../../../../Library/QueryDocument';

//Images
import Error from '../../../../../../Components/Images/Icon_Error_Red.svg';
import LoadingIcon from '../../../../../../Components/Images/Image_Loading_Ripple.svg';
import ClearSearch from '../../../../../../Components/Images/Icon_Clear_Grey.svg';
import SearchIcon from '../../../../../../Components/Images/Icon_Search_Grey.svg'


export default function AdminUserGroupsPane({
  title,
  defaultUsersQuery,
  paneOpen,
  setPaneOpen,
}) {

  //------------------------------------------------------
  //  useStates
  //------------------------------------------------------

  // Used to set pane status > 'pending', 'onload', 'error'
  const [paneStatus, setPaneStatus] = useState('pending');

  //------------------------------------------------------
  //  useReducer
  //------------------------------------------------------

  // Used to store details about the users
  const [userData, setUserData] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      'paginatedUsers': [],
      'addedUsers': [],
      'removedUsers': [],

      // Add Users Search
      'newUserInput': '',
      'queryMessage': '',
      'searchResults': []
    }
  );

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

  // Function used to handle search
  function handleUserSearch(value) {

    if (value.length <= 3) {

      setUserData({
        'queryMessage': 'Search requires more than three characters.',
        'searchResults': [],
      });

      return;

    }

    // Reset search
    setUserData({
      'queryMessage': '',
    });

    const query = value.toLowerCase();

    // Search by characters in email
    QueryDocument('users', [
      defaultUsersQuery,
      ['status', '==', 'active'],
      ['searcharray', 'array-contains-any', [query]]
    ]).then((users) => {

      setUserData({
        'searchResults': users,
        'queryMessage': `Found ${users.length} result(s)`
      });

    }).catch((error) => {

      console.log(error);
      setUserData({ 'queryMessage': 'Oops something went wrong!' });

    });

  }

  //------------------------------------------------------
  //  useEffect
  //------------------------------------------------------

  // Onload > Get the user docs
  //  - Limited to first 100
  useEffect(() => {

    if (defaultUsersQuery === undefined) return;

    QueryDocument('users', [
      defaultUsersQuery,
      ['status', '==', 'active'],
    ], ['emailaddress'], 100).then((users) => {

      setUserData({
        'paginatedUsers': users,
        'addedUsers': [],
        'removedUsers': [],
      });
      setPaneStatus('onload');

    }).catch((error) => {

      console.log('error', error);
      setPaneStatus('error');

    });

  }, [defaultUsersQuery]);

  //------------------------------------------------------
  //  Edit User Modal
  //------------------------------------------------------

  if (!paneOpen) return null;

  // ---------------------------------------------------
  //  Pending
  // ---------------------------------------------------

  if (paneStatus === 'pending') {

    return (
      <div className='Pane-Background'>

        {/* Modal Container */}
        <dialog className='Application-Details-Pane-Container flex flex-col min-w-[950px] items-center'>

          <img className='w-[200px] self-center' alt='loading-circle-icon' src={LoadingIcon}></img>

          <button className='Primary-Button w-fit self-center' onClick={() => setPaneOpen(false)}>
            Cancel
          </button>

        </dialog>

      </div>
    )

  }

  // ---------------------------------------------------
  //  Onload
  // ---------------------------------------------------

  else if (paneStatus === 'onload') {

    return (
      <div className='Pane-Background'>

        {/* Modal Container */}
        <dialog className='Application-Details-Pane-Container'>

          <div className='grid grid-rows-[170px_1fr_70px] items-center justify-between w-full overflow-hidden' >

            {/* ------------------------------------------------------ */}
            {/*  Header Container                                      */}
            {/* ------------------------------------------------------ */}

            <div className='flex flex-col gap-2'>

              {/* ------------------------------------------------------ */}
              {/*  Header                                                */}
              {/* ------------------------------------------------------ */}

              <div className='flex flex-col gap-1 px-4 py-2 border-solid border-b border-b-[#D8D8D8]'>

                <div className='flex flex-col gap-1'>
                  <h5>Group | {title}</h5>
                </div>

              </div>

              {/* ------------------------------------------------------ */}
              {/*  Add Users Search Bar                                  */}
              {/* ------------------------------------------------------ */}

              <div className='relative my-[10px]  px-4'>

                {/* Search Bar */}
                <div className='flex flex-row items-center'>

                  {/* Search Input Field */}
                  <div className='flex flex-row gap-2 w-full h-[45px] px-[15px] py-[10px] bg-[white] border border-solid border-[#7c7c7c] rounded-tl-[5px] '>

                    <img src={SearchIcon} alt='Search-Icon'></img>
                    <input
                      className='w-full focus:outline-none'
                      type='text'
                      placeholder='Search membership by email address'
                      value={userData?.newUserInput}
                      onChange={(evt) => {

                        const value = evt.target.value;
                        setUserData({ 'newUserInput': value });

                        // Hide search
                        if (value.length === 0) {

                          return setUserData({
                            'newUserInput': '',
                            'queryMessage': '',
                            'searchResults': []
                          });

                        }

                        // Trigger search
                        handleUserSearch(value);

                      }}
                      autoComplete='off'
                    ></input>

                  </div>


                  {/* Clear Search Button */}
                  {
                    (userData?.newUserInput.length || userData?.searchResults.length) > 0 &&

                    <div className='animate-fadein  bg-[#F1F1F1] h-[45px] p-[15px] shadow-md border border-solid border-[#080808] border-l-[none] rounded-tl-none rounded-br-none rounded-tr-[5px] rounded-bl-none text-center align-center cursor-pointer'>

                      <img className='self-center' src={ClearSearch} alt='ClearSearch' onClick={() =>
                        setUserData({
                          'newUserInput': '',
                          'queryMessage': '',
                          'searchResults': []
                        })
                      }></img>

                    </div>
                  }

                </div>

                {/* Search Results */}
                {
                  (userData?.searchResults.length > 0 || userData.queryMessage.length > 0) &&
                  (
                    <div className='absolute z-10 max-h-[350px] w-[calc(100%-48px)] overflow-y-auto bg-white shadow-lg border-[1px] border-[solid] border-[#D2D2D2]'>
                      {
                        userData?.searchResults.map((user, index) => (
                          <div key={index} className='border-b border-b-[#E1E1E1] last:border-0 hover:bg-[#F0F0F0]'>

                            <div className='flex flex-row justify-between py-[10px] px-[15px] cursor-not-allowed'>
                              <div className='flex flex-col'>
                                <p className='m-0 font-medium text-[14px] '>
                                  {user?.givenname} {user?.surname}
                                </p>
                                <p className='m-0 text-[14px] '>
                                  {user?.emailaddress} {(user?.jobtitle && user?.department) && `(${user?.jobtitle} | ${user?.department})`}
                                </p>
                              </div>

                            </div>

                          </div>
                        ))
                      }

                      {/* Total Search Results */}
                      <div className='text-sm py-[10px] px-[15px]'>
                        {userData.queryMessage}
                      </div>

                    </div>
                  )
                }

              </div>

            </div>

            {/* ------------------------------------------------------ */}
            {/*  Body Container                                        */}
            {/* ------------------------------------------------------ */}

            <div className='w-[88vw] h-full flex flex-col overflow-y-auto p-4 px-4 py-2 border-solid border shadow-inner'>

              {/* ------------------------------------------------------ */}
              {/*  Table Container                                       */}
              {/* ------------------------------------------------------ */}

              <div className='Table-Container overflow-hidden'>

                {/* Table header (Grey Section) */}
                <div className='flex flex-row justify-between items-center py-[10px] px-[20px] rounded-tl-[10px] rounded-tr-[10px] border-b-[#D8D8D8] border-solid border-b bg-[#F5F5F5]'>

                  {/* Total */}
                  <p className='m-0 font-medium '>
                    <span className='mr-[5px]'>Total:</span>

                    {userData.paginatedUsers.length} of 5,000+
    
                  </p>
                
                </div>

                {/* Makes the table scrollable */}
                <div className='overflow-auto h-full'>

                  <table className='table-fixed w-full'>

                    <colgroup>
                      <col span='1' style={{ width: '80px' }}></col>
                      <col span='1' style={{ width: '80px' }}></col>
                      <col span='1' style={{ width: '150px' }}></col>
                      <col span='1' style={{ width: '100px' }}></col>
                      <col span='1' style={{ width: '100px' }}></col>
                      <col span='1' style={{ width: '100px' }}></col>
                      <col span='1' style={{ width: '80px' }}></col>
                    </colgroup>

                    <thead>

                      {/* Sticky the header */}
                      <tr className='sticky top-0 drop-shadow-md bg-white border-b-[#D8D8D8] border-solid border-b rounded'>
                        <th className='py-[15px] px-[18px] font-semibold text-[#212529]'>First Name</th>
                        <th className='py-[15px] px-[18px] font-semibold text-[#212529]'>Last Name</th>
                        <th className='py-[15px] px-[18px] font-semibold text-[#212529]'>Email</th>
                        <th className='py-[15px] px-[18px] font-semibold text-[#212529]'>Job Title</th>
                        <th className='py-[15px] px-[18px] font-semibold text-[#212529]'>Department</th>
                        <th className='py-[15px] px-[18px] font-semibold text-[#212529]'>State</th>
                        <th className='py-[15px] px-[18px] font-semibold text-[#212529]'>Region</th>
                      </tr>

                    </thead>

                    <tbody>
                      {
                        userData?.paginatedUsers?.length > 0 ?
                          (
                            userData?.paginatedUsers?.map((user, index) => (
                              <tr key={index} className='border-b-[#D8D8D8] border-solid border-b rounded last:border-none'>
                                <td className='py-[15px] px-[18px] font-normal break-words align-top whitespace-nowrap overflow-hidden text-ellipsis'> {user?.givenname} </td>
                                <td className='py-[15px] px-[18px] font-normal break-words align-top whitespace-nowrap overflow-hidden text-ellipsis'> {user?.surname} </td>
                                <td className='py-[15px] px-[18px] font-normal break-words align-top whitespace-nowrap overflow-hidden text-ellipsis'> {user?.emailaddress} </td>
                                <td className='py-[15px] px-[18px] font-normal break-words align-top whitespace-nowrap overflow-hidden text-ellipsis'> {user?.jobtitle} </td>
                                <td className='py-[15px] px-[18px] font-normal break-words align-top whitespace-nowrap overflow-hidden text-ellipsis'> {user?.department} </td>
                                <td className='py-[15px] px-[18px] font-normal break-words align-top whitespace-nowrap overflow-hidden text-ellipsis'> {user?.state} </td>
                                <td className='py-[15px] px-[18px] font-normal break-words align-top whitespace-nowrap overflow-hidden text-ellipsis'> {user?.country} </td>
                              </tr>
                            ))

                          )
                          :
                          (
                            <tr className=''>
                              <td colSpan={8} className='py-4 px-3 font-normal break-words text-center align-top'> No users found.</td>
                            </tr>
                          )
                      }
                    </tbody>

                  </table>

                </div>

              </div>

            </div>

            {/* ------------------------------------------------------ */}
            {/*  Buttons Container                                     */}
            {/* ------------------------------------------------------ */}

            <div className='flex flex-row gap-2 px-4 py-3'>

              <button className='Primary-Button' onClick={() => {

                // Clear Search Results
                setUserData({
                  'newUserInput': '',
                  'queryMessage': '',
                  'searchResults': []
                });

                // Close Side Pane
                setPaneOpen(false);

              }}>
                Close
              </button>

            </div>

          </div>

        </dialog>

      </div>
    )

  }

  // ---------------------------------------------------
  //  Error
  // ---------------------------------------------------

  else if (paneStatus === 'error') {

    return (
      <div className='Modal-Background'>

        {/* Modal Container */}
        <dialog className='Application-Details-Pane-Container flex flex-col min-w-[950px] justify-center items-center'>

          <img className='my-2 w-[50px]' src={Error} alt='Error'></img>
          <h4>Oops! Something went wrong.</h4>
          <p className='leading-[1.7]'>
            There was an error loading this page.
          </p>
          <button className='Primary-Button w-fit' onClick={() => setPaneOpen(undefined)}>Back</button>

        </dialog>

      </div>

    )

  }

  //------------------------------------------------------

}
